import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, MessageCircle, ArrowLeft, Boxes } from "lucide-react";
import { api, whatsappLink } from "@/lib/api";
import { useSEO } from "@/lib/seo";
import { useQuote } from "@/context/QuoteContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export default function ProductDetail() {
  const { slug } = useParams();
  const { openQuote } = useQuote();
  const [product, setProduct] = useState(null);
  const [active, setActive] = useState(0);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setProduct(null);
    setActive(0);
    api.get(`/products/${slug}`).then((r) => setProduct(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  useSEO({ title: product?.name || "Product", description: product?.description });

  if (notFound) {
    return (
      <div className="min-h-screen grid place-items-center px-5" data-testid="product-not-found">
        <div className="text-center">
          <h1 className="font-display text-3xl text-forest">Product not found</h1>
          <Link to="/products"><Button className="mt-4 rounded-full bg-forest text-cream">Back to Products</Button></Link>
        </div>
      </div>
    );
  }
  if (!product) return <div className="min-h-screen grid place-items-center text-forest">Loading…</div>;

  return (
    <div className="pt-28 pb-24" data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <Link to="/products" className="inline-flex items-center gap-2 text-forest/70 hover:text-gold transition-colors mb-8 text-sm" data-testid="back-to-products">
          <ArrowLeft size={16} /> Back to Products
        </Link>
        <div className="grid lg:grid-cols-2 gap-12">
          <div>
            <motion.div key={active} initial={{ opacity: 0.4 }} animate={{ opacity: 1 }} className="overflow-hidden rounded-2xl h-[440px] shadow-xl">
              <img src={product.images?.[active]} alt={product.name} className="w-full h-full object-cover" />
            </motion.div>
            {product.images?.length > 1 && (
              <div className="flex gap-3 mt-4">
                {product.images.map((img, i) => (
                  <button key={i} onClick={() => setActive(i)} data-testid={`thumb-${i}`}
                    className={`w-20 h-20 rounded-xl overflow-hidden border-2 transition-colors ${i === active ? "border-gold" : "border-transparent"}`}>
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <span className="text-olive text-xs font-semibold tracking-[0.25em] uppercase">{product.category}</span>
            <h1 className="font-display text-4xl lg:text-5xl text-forest mt-2 leading-tight">{product.name}</h1>
            {product.tagline && <p className="text-gold font-medium mt-2">{product.tagline}</p>}
            <p className="text-muted-foreground mt-5 leading-relaxed">{product.description}</p>

            {product.features?.length > 0 && (
              <div className="mt-7">
                <h3 className="font-display text-lg text-forest mb-3">Key Features</h3>
                <div className="grid sm:grid-cols-2 gap-2.5">
                  {product.features.map((f) => (
                    <div key={f} className="flex items-center gap-2 text-sm text-forest/80">
                      <Check size={16} className="text-olive shrink-0" /> {f}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-7 flex flex-wrap gap-6">
              {product.pack_sizes?.length > 0 && (
                <div>
                  <h4 className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Available Pack Sizes</h4>
                  <div className="flex flex-wrap gap-2">
                    {product.pack_sizes.map((s) => <Badge key={s} variant="outline" className="border-forest/20 text-forest">{s}</Badge>)}
                  </div>
                </div>
              )}
              {product.moq && (
                <div>
                  <h4 className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Minimum Order</h4>
                  <div className="flex items-center gap-2 text-forest font-medium"><Boxes size={16} className="text-gold" /> {product.moq}</div>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-3 mt-9">
              <Button onClick={() => openQuote({ product: product.name, title: `Quote — ${product.name}` })} data-testid="detail-quote-btn"
                className="rounded-full bg-forest hover:bg-olive text-cream px-8 h-12 transition-colors">Request Wholesale Quote</Button>
              <a href={whatsappLink(`Hi, I'm interested in ${product.name}. Please share bulk pricing.`)} target="_blank" rel="noreferrer" data-testid="detail-wa-btn">
                <Button variant="outline" className="rounded-full border-[#25D366] text-[#128C4B] hover:bg-[#25D366] hover:text-white px-6 h-12 transition-colors">
                  <MessageCircle size={18} className="mr-2" /> WhatsApp Enquiry
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
